<script>
// AppBreadcrumbs.vue
// Renders a hierarchy breadcrumb trail derived from the matched route records
// plus a back button on the left. No manual push/pop needed -- the breadcrumb
// chain is built automatically from the route tree as defined in the router.
//
// Usage: drop <AppBreadcrumbs /> anywhere in Dashboard.vue's template.
//
// Dynamic labels: components that load entity data (client name, pet name, etc.)
// should call this.$store.commit('setBreadcrumbLabel', { routeName, label })
// after their API response. Until then the static fallback label from
// meta.breadcrumb.label is displayed.

export default {
  name: 'AppBreadcrumbs',

  computed: {
    // Build the breadcrumb items from $route.matched, filtering to only the
    // route records that have meta.breadcrumb defined. Each item exposes:
    //   - label   : string to display (dynamic override or static fallback)
    //   - route   : the route record (used to build the router-link target)
    //   - isLast  : boolean -- last item is rendered as plain text, not a link
    breadcrumbItems() {
      const dynamicLabels = this.$store.state.dynamicBreadcrumbLabels;

      const items = this.$route.matched
        .filter(record => record.meta && record.meta.breadcrumb)
        .map(record => {
          const config = record.meta.breadcrumb;
          // Use the dynamic store label when the route is flagged dynamic and
          // the store has already received a label for this route name.
          const label =
            config.dynamic && dynamicLabels[record.name]
              ? dynamicLabels[record.name]
              : config.label;

          return {
            label,
            routeName: record.name,
            // Preserve the current params so links to ancestor segments stay
            // valid (e.g. clicking "Franquicia" keeps the :franchiseId param).
            params: this.$route.params,
          };
        });

      return items.map((item, index) => ({
        ...item,
        isLast: index === items.length - 1,
      }));
    },

    // Show the back button only when there is browser history to go back to.
    // window.history.length === 1 means this was the first page loaded in the
    // tab (direct URL navigation), so going back would leave the app entirely.
    showBackButton() {
      return window.history.length > 1;
    },

    // No breadcrumbs to display (e.g. the home page has only one item which
    // is the current page itself, so it renders as plain text with no links
    // above it -- but we still render the component to keep the back button).
    hasBreadcrumbs() {
      return this.breadcrumbItems.length > 0;
    },
  },

  methods: {
    goBack() {
      this.$router.back();
    },
  },
};
</script>

<template>
  <nav
    v-if="hasBreadcrumbs || showBackButton"
    aria-label="Breadcrumb"
    class="flex items-center gap-2 flex-wrap mb-2 text-sm"
  >
    <!-- Back button -->
    <button
      v-if="showBackButton"
      type="button"
      aria-label="Volver"
      class="flex items-center justify-center w-6 h-6 rounded hover:bg-primary-100 text-content-400 hover:text-primary-600 transition-colors flex-shrink-0"
      @click="goBack"
    >
      <!-- Left chevron icon -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="w-4 h-4"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fill-rule="evenodd"
          d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
          clip-rule="evenodd"
        />
      </svg>
    </button>

    <!-- Breadcrumb trail -->
    <ol
      v-if="hasBreadcrumbs"
      class="flex items-center flex-wrap gap-1"
    >
      <li
        v-for="(item, index) in breadcrumbItems"
        :key="item.routeName"
        class="flex items-center"
      >
        <!-- Chevron separator before every item except the first -->
        <span
          v-if="index > 0"
          class="mx-1 text-primary-300 select-none"
          aria-hidden="true"
        >›</span>

        <!-- Current page: plain text, marked as current in a11y -->
        <span
          v-if="item.isLast"
          class="text-primary-600 font-medium"
          aria-current="page"
        >{{ item.label }}</span>

        <!-- Ancestor: clickable router-link -->
        <router-link
          v-else
          :to="{ name: item.routeName, params: item.params }"
          class="text-content-400 hover:text-primary-500 hover:underline transition-colors"
        >{{ item.label }}</router-link>
      </li>
    </ol>
  </nav>
</template>
